import type { ReactNode } from "react";
import { ChevronsLeft, ChevronsRight } from "lucide-react";
import { IconButton } from "@/components/ui/icon-button";
import { RowButton } from "@/components/ui/pressable";
import { cn } from "@/lib/utils";

/**
 * Board lane shared by Tickets, Projects and Leads: a soft gray panel with
 * dot · label · count on top, hover actions, and an optional collapsed strip.
 * `children` is the lane body (usually a Droppable with the cards).
 */
export const BoardColumn = ({
  label,
  color,
  count,
  meta,
  actions,
  collapsed = false,
  onToggleCollapse,
  className,
  children,
}: {
  label: string;
  color?: string;
  count: ReactNode;
  /** Small text after the count (e.g. a stage total). */
  meta?: ReactNode;
  /** Extra hover buttons before collapse (e.g. "+"). */
  actions?: ReactNode;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
  children: ReactNode;
}) => {
  const dot = color ? (
    <span
      aria-hidden
      className="size-2 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  ) : null;

  return (
    <div
      className={cn(
        "group/column flex h-full min-h-0 flex-col overflow-hidden rounded-xl bg-muted/60",
        // Lanes share the full width; a collapsed one is a strip.
        collapsed ? "w-11 shrink-0" : "min-w-[17rem] flex-1 basis-0",
        className,
      )}
    >
      {collapsed ? (
        <RowButton
          layout="none"
          density="none"
          onClick={onToggleCollapse}
          className="flex flex-col items-center gap-2 px-1 pt-3 pb-2 text-xs text-muted-foreground hover:text-foreground"
          aria-label={`Expand ${label}`}
          title={`Expand ${label}`}
        >
          <ChevronsRight className="size-3.5" aria-hidden />
          {dot}
          <span className="tabular-nums">{count}</span>
          <span className="font-medium text-foreground [writing-mode:vertical-rl]">
            {label}
          </span>
        </RowButton>
      ) : (
        <div
          data-kanban-header
          className="flex shrink-0 select-none items-center gap-2 px-3 pt-2.5 pb-0.5"
        >
          {dot}
          <h3 className="truncate text-xs font-medium text-foreground">
            {label}
          </h3>
          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
            {count}
          </span>
          {meta ? (
            <span className="truncate text-xs text-muted-foreground">
              · {meta}
            </span>
          ) : null}
          {actions || onToggleCollapse ? (
            <span className="ml-auto flex shrink-0 items-center opacity-0 transition-opacity group-hover/column:opacity-100 focus-within:opacity-100">
              {actions}
              {onToggleCollapse ? (
                <IconButton
                  className="size-6 text-muted-foreground hover:text-foreground"
                  aria-label={`Collapse ${label}`}
                  title={`Collapse ${label}`}
                  onClick={onToggleCollapse}
                >
                  <ChevronsLeft className="size-3.5" />
                </IconButton>
              ) : null}
            </span>
          ) : null}
        </div>
      )}
      {children}
    </div>
  );
};

/** Dashed hint shown in an empty lane. */
export const BoardDropHint = ({ children }: { children: ReactNode }) => (
  <p className="rounded-lg border border-dashed border-border px-2 py-6 text-center text-xs text-muted-foreground">
    {children}
  </p>
);
